// src/App.jsx
/**
 * Application entry point.
 *
 * Responsibilities
 * ────────────────
 *  • Mount the two possible nav variants (`<NavBar/> | <MDNavBar/>`).
 *  • Gate protected routes behind `<ProtectedRoute/>`.
 *  • Lazy-load page modules to keep initial bundle size low.
 *  • Redirect unmatched paths to `/`.
 *  • Run a quick device-capability check (`useDeviceCompatibility`). On
 *    unsupported devices we replace the `/auth/*` subtree with a
 *    compatibility-warning page.
 *
 * Layout skeleton
 *  <div class="h-[100dvh] flex flex-col">   ← fills viewport height
 *    {NavBar}
 *    <Suspense>  ← lazy pages rendered inside
 *       <Routes> … </Routes>
 *    </Suspense>
 *  </div>
 *
 * Notes
 *  • Use `100dvh` (dynamic viewport unit) so Safari iOS 15+ doesn’t crop
 *    the content when the URL bar shrinks.
 *  • `typography` Tailwind plugin class gives consistent heading / paragraph
 *    defaults across pages.
 */

import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

/* ---------------------------------------- FIREBASE */
import { firebaseApp } from "./firebase/firebaseApp";
import { getAuth, signOut } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFirestore } from "firebase/firestore";

/* ---------------------------------------- PAGES */
import HomePage from "./pages/HomePage";
import FormPage from "./pages/FormPage";
import QuestionnairesPage from "./pages/QuestionnairesPage";
import ConfirmMetadataPage from "./pages/ConfirmMetadataPage";
import NoticePage from "./pages/NoticePage";
import AboutPage from "./pages/AboutPage";
import ResultsPage from "./pages/ResultsPage";
import ResultPage from "./pages/ResultPage";
import GuidancePage from "./pages/GuidancePage";
import GeneralGuidancePage from "./pages/GeneralGuidancePage";
import SpirometryGuidancePage from "./pages/SpirometryGuidancePage";
import CardiopulmonaryGuidancePage from "./pages/CardiopulmonaryGuidancePage";
/* ---------------------------------------- COMPONENTS */
import NavBar from "./components/navigation/NavBar";
import MDNavBar from "./components/navigation/MDNavBar";
import ForgotPasswordRequest from "./components/authentication/ForgotPasswordRequest";
import ResetPassword from "./components/authentication/ResetPassword";
import { CompatibleRoute } from "./components/navigation/CompatibleRoute";
import GlobalUploadToast from "./components/common/GlobalUploadToast";
import CookieConsentBanner from "./components/consent/CookieConsentBanner";
import AnalyticsGate from "./components/consent/AnalyticsGate";
import { ProtectedRoute } from "./components/authentication/ProtectedRoute";

/* ---------------------------------------- CONTEXT */
import { useConsent } from "./context/ConsentContext";
import { useAuth } from "./context/AuthContext";
import { useFirstRun } from "./context/FirstRunContext";
import { useProfilePrefetch } from "./context/ProfilePrefetchContext";
import { UserClinicMappingProvider } from "./context/UserClinicMappingContext";

/* ---------------------------------------- UTILS */
import { useSyncHtmlLang } from "./utils/useSyncHtmlLang";
import { useNavVariant } from "./utils/navigation/useNavVariant";
import { analytics, trackPageView } from "./utils/analytics/track";
import { MEASUREMENT_VARIANTS, QUESTIONNAIRES } from "./utils/constants";
import { DEFAULT_RECORDING_MODES } from "./utils/recordingModes";

/* ---------------------------------------- HOOKS */
import useFirstRunLoginPrefetch from "./hooks/useFirstRunLoginPrefetch";
import useAuthCacheHygiene from "./hooks/useAuthCacheHygiene";
import useProfileLoginPrefetch from "./hooks/useProfileLoginPrefetch";
import useUploadToasts from "./hooks/useUploadToasts";
import useBrowserCompatibility from "./hooks/useBrowserCompatibility";
import { useRecordingModeConfig } from "./hooks/useRecordingModeConfig";

/* ---------------------------------------- ROUTES */
import RecordingSpirometryRouter from "./routes/RecordingSpirometryRouter";
import RecordingTidalBreathingRouter from "./routes/RecordingTidalBreathingRouter";
import RecordingModeGate from "./components/recording/RecordingModeGate";
import RecordingManualAudioRouter from "./routes/RecordingManualAudioRouter";
import { useUserClinicMapping } from "./hooks/useUserClinicMapping";
import { getGuidePathFromCameraPath } from "./utils/navigation/recordingGuideNav";
import { useLastSafeRoute } from "./hooks/useLastSafeRoute";
import BottomNavBar from "./components/navigation/BottomNavBar";
import { MobileMenuOverlay } from "./components/navigation/MobileMenuOverlay";
import { MenuPanel } from "./components/navigation/MenuPanel";

/* ------------------------------------------------------------------ */
/* Lazy-loaded pages (code-splitting)                                 */
/* ------------------------------------------------------------------ */
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const CompatibilityPage = lazy(() => import("./pages/CompatibilityPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const BrowserCompatibilityPage = lazy(
  () => import("./pages/BrowserCompatibilityPage"),
);

const DEFAULT_PERMITTED_QUESTIONNAIRES = ["sgrq"];

/* ------------------------------------------------------------------ */

function isStandaloneDisplayMode() {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.matchMedia?.("(display-mode: standalone)")?.matches) {
    return true;
  }

  if (window.matchMedia?.("(display-mode: fullscreen)")?.matches) {
    return true;
  }

  return window.navigator?.standalone === true;
}

export default function App() {
  const auth = getAuth(firebaseApp);
  const storage = getStorage(firebaseApp);
  const db = getFirestore(firebaseApp);
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Decide which nav bar variant to render based on location auth state.
  const navVariant = useNavVariant(); // 'main' | 'landing'

  const {
    ensureProfile,
    ensureProfileInfo,
    getCachedProfileInfo,
    clearProfile,
    clearAllProfiles,
  } = useProfilePrefetch();

  useProfileLoginPrefetch({
    uid: user?.uid,
    ensureProfileInfo,
    ensureProfile,
    getCachedProfileInfo,
    ttlMs: 10 * 60 * 1000,
    recertMs: 3 * 30 * 24 * 60 * 60 * 1000,
  });

  const mapping = useUserClinicMapping({ uid: user?.uid });

  const {
    loading: loadingModeConfig,
    mappingResolved,
    clinicStudyId,
    config,
  } = useRecordingModeConfig({ uid: user?.uid, mapping });

  // IMPORTANT: distinguish "unknown" from "unrestricted"
  const permittedModes = Array.isArray(config?.permittedModes)
    ? config.permittedModes
    : null;
  const configuredPermittedQuestionnaires = Array.isArray(
    config?.permittedQuestionnaires,
  )
    ? config.permittedQuestionnaires
    : null;
  const knownQuestionnaireSlugs = useMemo(
    () => QUESTIONNAIRES.map(({ slug }) => slug),
    [],
  );

  const modesReady = mappingResolved && !loadingModeConfig;

  const isPermitted = (mode) => {
    if (!modesReady) return false;

    if (clinicStudyId && permittedModes) return permittedModes.includes(mode);

    if (DEFAULT_RECORDING_MODES.includes(mode)) return true;

    return false;
  };

  const permittedQuestionnaires = useMemo(() => {
    if (!modesReady) return [];

    const fallbackQuestionnaires = knownQuestionnaireSlugs.filter((slug) =>
      DEFAULT_PERMITTED_QUESTIONNAIRES.includes(slug),
    );

    if (clinicStudyId && configuredPermittedQuestionnaires) {
      return knownQuestionnaireSlugs.filter((slug) =>
        configuredPermittedQuestionnaires.includes(slug),
      );
    }

    return fallbackQuestionnaires;
  }, [
    modesReady,
    clinicStudyId,
    configuredPermittedQuestionnaires,
    knownQuestionnaireSlugs,
  ]);

  const isQuestionnairePermitted = useCallback(
    (slug) => permittedQuestionnaires.includes(slug),
    [permittedQuestionnaires],
  );

  const { ensureFirstRun, getCachedFirstRun, clearAllFirstRun } = useFirstRun();

  useFirstRunLoginPrefetch({
    uid: user?.uid,
    db,
    ensureFirstRun,
    variants: MEASUREMENT_VARIANTS,
    ttlMs: 10 * 60 * 1000,
    // requireCompleted: true, // enable if you store status == "completed"
  });

  const firstRunEntry = user?.uid ? getCachedFirstRun(user.uid) : null;
  // While the weekly reminder check is loading, default to skipping the guide.
  // Once ready, home tiles will use the real per-variant reminder value.
  const firstRunByVariant =
    firstRunEntry?.status === "ready" && firstRunEntry?.value?.byVariant
      ? firstRunEntry.value.byVariant
      : Object.fromEntries(MEASUREMENT_VARIANTS.map((v) => [v, false]));

  useAuthCacheHygiene({
    uid: user?.uid,
    clearProfile,
    clearAllProfiles,
    clearAllFirstRun,
  });

  const handleSignOut = useCallback(() => {
    try {
      if (clearAllProfiles) clearAllProfiles();
      else if (user?.uid) clearProfile(user.uid);
    } catch {
      // ignore
    }

    signOut(getAuth(firebaseApp))
      .then(() => navigate("/auth/login", { replace: true }))
      .catch(
        (err) => process.env.NODE_ENV === "development" && console.error(err),
      );
  }, [navigate, clearAllProfiles, clearProfile, user?.uid]);

  useSyncHtmlLang();
  const params = new URLSearchParams(window.location.search);
  const forceCompat = params.get("forceCompat"); // e.g. in_app_browser | webview | safari_webkit | any

  const detectedBrowserInfo = useBrowserCompatibility(); // always call hook

  const forcedInfo = forceCompat
    ? {
        isSupported: false,
        reasons: [forceCompat],
        browser: "Forced",
        context: "forced",
      }
    : null;

  const browserInfo = forcedInfo || detectedBrowserInfo;

  // Enforce on deep-link sensitive entry points (QR / recording flows)
  const pathname = window.location.pathname || "";
  const isSensitiveEntry =
    pathname.startsWith("/recording") || pathname.startsWith("/auth");

  const shouldShowBrowserWarning =
    isSensitiveEntry &&
    !browserInfo.isSupported &&
    !pathname.startsWith("/compatibility"); // avoid loops

  const { toastModel, onUploadStatus, onUploadProgress } = useUploadToasts();

  const location = useLocation();
  const consent = useConsent();

  useEffect(() => {
    if (consent?.status === "set" && consent?.analytics) {
      trackPageView(location.pathname + location.search);
    }
  }, [location.pathname, location.search, consent?.status, consent?.analytics]);

  const guidePath = useMemo(() => {
    return getGuidePathFromCameraPath(location?.pathname || "");
  }, [location?.pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const path = location?.pathname || "";
  const isRecordingRoute = path.startsWith("/recording/");
  const isFormRoute = path.startsWith("/form");
  const shouldOpenHomeOnStandaloneLaunch =
    path === "/" && Boolean(user) && isStandaloneDisplayMode();
  const spirometryVideoRetentionMode =
    config?.spirometryVideoRetentionMode || "full-frame";

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useLastSafeRoute();

  const tidalRecordingDurationSeconds = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("recordingDurationSeconds");
    const parsed = Number(raw);

    if (!Number.isFinite(parsed)) return 60;

    return parsed;
  }, [location.search]);

  return (
    <UserClinicMappingProvider value={mapping}>
      <div className="h-[100dvh] flex flex-col typography">
        {navVariant === "main" ? (
          <NavBar
            menuOpen={menuOpen}
            onToggleMenu={() => setMenuOpen((o) => !o)}
            onCloseMenu={() => setMenuOpen(false)}
            guidePath={guidePath}
            handleSignOut={handleSignOut}
          />
        ) : (
          <MDNavBar />
        )}

        {/* Consent banner (shown only when status !== 'set') */}
        <CookieConsentBanner />

        {/* Loads analytics only after analytics consent */}
        <AnalyticsGate />

        <GlobalUploadToast toastModel={toastModel} />

        {/* Suspense boundary – shows fallback while lazy chunks download */}
        <div className="flex flex-col items-center justify-start h-full overflow-y-scroll">
          <Suspense fallback={<div className="m-auto">Loading…</div>}>
            {shouldShowBrowserWarning ? (
              <BrowserCompatibilityPage info={browserInfo} />
            ) : (
              <Routes>
                {/* ---------- Public routes ---------- */}
                <Route
                  path="/"
                  element={
                    shouldOpenHomeOnStandaloneLaunch ? (
                      <Navigate to="/home" replace />
                    ) : (
                      <LandingPage />
                    )
                  }
                />
                <Route
                  path="/auth/*"
                  // element={<AuthPage />}
                  element={<AuthPage />}
                />
                <Route
                  path="/auth/forgot-password"
                  element={<ForgotPasswordRequest />}
                />
                <Route
                  path="/auth/reset-password"
                  element={<ResetPassword />}
                />
                <Route path="/about" element={<AboutPage />} />
                {/* ---------- Protected routes ---------- */}
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      <HomePage
                        firstRunByVariant={firstRunByVariant}
                        isPermitted={isPermitted}
                        hasQuestionnaires={permittedQuestionnaires.length > 0}
                        loadingModes={!modesReady}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questionnaires/:questionnaireName?"
                  element={
                    <ProtectedRoute>
                      <QuestionnairesPage
                        permittedQuestionnaires={permittedQuestionnaires}
                        isQuestionnairePermitted={isQuestionnairePermitted}
                        loadingQuestionnaires={!modesReady}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/notice?"
                  element={
                    <ProtectedRoute>
                      <NoticePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/results"
                  element={
                    <ProtectedRoute>
                      <ResultsPage
                        db={db}
                        user={user}
                        isPermitted={isPermitted}
                        loadingModes={!modesReady}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/result"
                  element={
                    <ProtectedRoute>
                      <ResultPage
                        db={db}
                        user={user}
                        isPermitted={isPermitted}
                        loadingModes={!modesReady}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guidance"
                  element={
                    <ProtectedRoute>
                      <GuidancePage isPermitted={isPermitted} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guidance/general"
                  element={
                    <ProtectedRoute>
                      <GeneralGuidancePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guidance/spirometry"
                  element={
                    <ProtectedRoute>
                      <SpirometryGuidancePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/guidance/cardiopulmonary"
                  element={
                    <ProtectedRoute>
                      <CardiopulmonaryGuidancePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/form"
                  element={
                    <ProtectedRoute>
                      <FormPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/about" element={<AboutPage />} />

                <Route
                  path="/recording"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <Navigate to="/" replace />
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/spirometry/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("spirometry")}
                          defaultMode={config?.defaultMode}
                        >
                          <RecordingSpirometryRouter
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            firstRun={
                              // true
                              !!firstRunByVariant["RecordingSpirometry"]
                            }
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            analytics={analytics}
                            appMode={"spirometry"}
                            subjectId={mapping?.subjectId}
                            showLiveChart={false}
                            enablePixelSignal={false}
                            enableGrid3x3Pixel={false}
                            showTrackedPoints={false}
                            poseDebug={false}
                            traceMode={"live"}
                            maxTrackedPoints={24}
                            includePreExhale={true}
                            videoRetentionMode={spirometryVideoRetentionMode}
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/tidal-breathing/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("tidal-breathing")}
                          defaultMode={config?.defaultMode}
                        >
                          <RecordingTidalBreathingRouter
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            firstRun={
                              !!firstRunByVariant["RecordingTidalBreathing"]
                            }
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            analytics={analytics}
                            variant={"RecordingTidalBreathing"}
                            showLiveChart={false}
                            appMode={"respiratory"}
                            subjectId={mapping?.subjectId}
                            enablePixelSignal={false}
                            enableGrid3x3Pixel={false}
                            recordingDurationSeconds={
                              tidalRecordingDurationSeconds
                            }
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/manual-audio/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("manual-audio")}
                          defaultMode={config?.defaultMode}
                        >
                          <RecordingManualAudioRouter
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            firstRun={
                              !!firstRunByVariant["RecordingManualAudio"]
                            }
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            analytics={analytics}
                            variant={"RecordingManualAudio"}
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/recording/post-exertion/*"
                  element={
                    <CompatibleRoute>
                      <ProtectedRoute>
                        <RecordingModeGate
                          allowed={isPermitted("post-exertion")}
                          defaultMode={config?.defaultMode}
                        >
                          <RecordingTidalBreathingRouter
                            auth={auth}
                            storage={storage}
                            db={db}
                            user={user}
                            onUploadStatus={onUploadStatus}
                            onUploadProgress={onUploadProgress}
                            analytics={analytics}
                            showLiveChart={false}
                            appMode={"post-exertion"}
                            subjectId={mapping?.subjectId}
                            enablePixelSignal={false}
                            enableGrid3x3Pixel={false}
                            firstRun={
                              !!firstRunByVariant["RecordingPostExertion"]
                            }
                            variant={"RecordingPostExertion"}
                            recordingDurationSeconds={180}
                          />
                        </RecordingModeGate>
                      </ProtectedRoute>
                    </CompatibleRoute>
                  }
                />

                <Route
                  path="/review-metadata"
                  element={
                    <ProtectedRoute>
                      <ConfirmMetadataPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/compatibility" element={<CompatibilityPage />} />

                {/* ---------- Fallback ---------- */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            )}
          </Suspense>
        </div>
        {menuOpen && (
          <>
            {/* Mobile overlay */}
            <MobileMenuOverlay
              onClose={() => setMenuOpen(false)}
              guidePath={guidePath}
              handleSignOut={handleSignOut}
              showQuestionnaires={permittedQuestionnaires.length > 0}
            />
            {/* Desktop popdown */}
            <div
              className="hidden md:block fixed left-0 right-0 z-40"
              style={{ top: "calc(44px + env(safe-area-inset-top, 0px))" }}
            >
              {/* optional backdrop just behind dropdown */}
              <button
                type="button"
                className="absolute inset-0 bg-transparent"
                style={{
                  height: "calc(100dvh - 44px - env(safe-area-inset-top, 0px))",
                }}
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              />
              <div className="relative mx-auto w-full flex justify-center bg-container border-b border-greyscale-3  shadow-lg p-4">
                <MenuPanel
                  onClose={() => setMenuOpen(false)}
                  handleSignOut={handleSignOut}
                  guidePath={guidePath}
                  showQuestionnaires={permittedQuestionnaires.length > 0}
                  dense
                />
              </div>
            </div>
          </>
        )}
        {navVariant === "main" && !isRecordingRoute && !isFormRoute ? (
          <BottomNavBar
            menuOpen={menuOpen}
            onToggleMenu={() => setMenuOpen((o) => !o)}
            onCloseMenu={() => setMenuOpen(false)}
          />
        ) : null}
      </div>
    </UserClinicMappingProvider>
  );
}
